import {
  PolicyExecutionStatus,
  ConfigActionTransferToAgent,
  ConfigActionTransferToSkill,
  ConfigActionSendMessage,
  ConfigRuleActionType,
  ConfigActionExecuteFaaS,
} from '@liveperson/lpcontextschemalib';
import { PolicyEvalDisposition } from '../../../enums';
import { formatConditionString } from './formatters';
import { PolicyActionDisplay } from '..';
import {
  removeEnclosingQuotes,
  functionArgumentsRegEx,
  functionArgumentSplitingRegEx,
  functionRegEx,
  functionRegExGlobal,
} from '../../../util';

export interface PolicyActionResultParserProps {
  result?: PolicyExecutionStatus | Array<ConfigActionSendMessage
  | ConfigActionTransferToAgent | ConfigActionTransferToSkill |
  ConfigActionExecuteFaaS>;
  exception?: any;
}

export const policyActionResultParser = ({
  result, exception,
}: PolicyActionResultParserProps): PolicyEvalDisposition => {
  if (exception) {
    if (exception.startsWith('parse error')) {
      return PolicyEvalDisposition.INVALID;
    }
    return PolicyEvalDisposition.EXCEPTION;
  }

  // if this is an array it means that the policy evaluated to true
  // and some action was executed
  if (Array.isArray(result)) {
    return PolicyEvalDisposition.MATCH;
  }

  switch (result) {
    case PolicyExecutionStatus.CONDITIONFAILED:
    case PolicyExecutionStatus.NOENABLEDPOLICY:
    case PolicyExecutionStatus.NOPOLICYDEFINED:
      return PolicyEvalDisposition.NO_MATCH;
    case PolicyExecutionStatus.DISABLED:
      return PolicyEvalDisposition.DISABLED;
    case PolicyExecutionStatus.UNKNOWNACTION:
    case PolicyExecutionStatus.INVALIDACTIONREFERENCE:
    case PolicyExecutionStatus.EVALEXCEPTION:
      return PolicyEvalDisposition.EXCEPTION;
    default:
      return PolicyEvalDisposition.NO_MATCH;
  }
};

export const singlePolicyActionParser = (policyAction: ConfigActionSendMessage |
ConfigActionTransferToAgent | ConfigActionTransferToSkill |
ConfigActionExecuteFaaS | PolicyExecutionStatus): PolicyActionDisplay => {
  if ((policyAction as ConfigActionSendMessage |
  ConfigActionTransferToAgent | ConfigActionTransferToSkill |
  ConfigActionExecuteFaaS).type) {
    switch ((policyAction as ConfigActionSendMessage |
    ConfigActionTransferToAgent | ConfigActionTransferToSkill |
    ConfigActionExecuteFaaS).type) {
      case ConfigRuleActionType.SEND_MESSAGE:
        return {
          title: 'Send message',
          message: `"${(policyAction as ConfigActionSendMessage).payload.message}"`,
        };
      case ConfigRuleActionType.TRANSFER_TO_AGENT:
        return {
          title: 'Transfer to agent',
          message: `${(policyAction as ConfigActionTransferToAgent).payload.userId}`,
        };
      case ConfigRuleActionType.TRANSFER_TO_SKILL:
        return {
          title: 'Transfer to skill',
          message: `${(policyAction as ConfigActionTransferToSkill).payload.skillId}`,
        };
      case ConfigRuleActionType.EXECUTE_FAAS: // not yet supported
      default:
        return {
          title: '',
          message: '',
        };
    }
  }

  return {
    title: '—',
    message: '',
  };
};

export interface IParseConditionToWordsProps {
  condition?: string;
  variables?: Array<string>
  transientVariableNames?: Array<string>
  valueMap?: { [key: string]: any };
}

export interface IConvertTimeBasedFunctionFromConditionToVarProps {
  str: string;
  valueMap: { [key: string]: any };
}

/**
 *
 * @param str - is condition string
 * @param valueMap - is a dictionary with
 * key: variables and transientVariables names & value: variables and transientVariables values
 * @returns fnName(arg1, 'hi', 'foo') -> fnName(foo,bar,baz)
 */
export const convertTimeBasedFunctionFromConditionToVar = ({
  str, valueMap,
}: IConvertTimeBasedFunctionFromConditionToVarProps) => {
  const matchRes = str.match(functionRegEx);
  let testTransientVariableKey = '';
  if (matchRes) {
    // split functiion name and function arguments
    // 'doSomething(arg1, 'h1', 'foo')' --> 'doSomething' & '(arg1, 'h1', 'foo', ['a', 'b'])'
    const [, fnName, argListStr] = matchRes;
    // split arguments into an array of arguments
    // '(arg1, 'h1', 'foo')' --> [arg1, 'hi', 'foo', ['a', 'b']]
    const argsList = argListStr.split(functionArgumentSplitingRegEx);
    // reconstruct function signature using fnName and argsList
    // fnName = 'doSomething' argsList = [arg1, 'hi', 'foo'] --> doSomething(arg1,hi,foo,[a,b])
    testTransientVariableKey += `${fnName}(`;
    argsList.forEach((arg: string, argIdx: number) => {
      // remove single quotes from the argument
      const strippedArg = arg.replace(/'/g, '"');

      try {
        const parsedRes = JSON.parse(strippedArg.trim());
        // if the argument is an array then parse it
        // ['a', 'b'] --> [a,b]
        if (Array.isArray(parsedRes)) {
          testTransientVariableKey += `[${parsedRes}]`;
          if (argIdx < argsList.length - 1) {
            testTransientVariableKey += ',';
          }
          return;
        }
      } catch (err) {
        // Note: Do nothing
        // array parsing failed
        // throw new Error(`Unable to parse argument: ${strippedArg}`);
        // console.error('cannot parse array arguments', err);
      }

      if (valueMap[strippedArg] != null) {
        testTransientVariableKey += `${valueMap[strippedArg]}`;
      } else {
        testTransientVariableKey += removeEnclosingQuotes(strippedArg.trim());
      }
      if (argIdx < argsList.length - 1) {
        testTransientVariableKey += ',';
      }
    });
    testTransientVariableKey += ')';
  }
  return testTransientVariableKey;
};

/**
 *
 * @param condition - is condition string
 * @param variables - is an array of variable names
 * @param transientVariableNames - is an array of transient variable names
 * @param valueMap - is a dictionary with
 * key: variables and transientVariables names & value: variables and transientVariables values
 * @returns an array of words in which known variables are now denoted by index of their
 * corresponding values in variables or transientVariableNames array
 * input: "custom.foo == 'foo' and fnName(arg1, arg2, arg3, arg4)"
 * output: [$v:0$, and, $t:0$, (arg1, arg2, arg3, arg4)]
 */
export const parseConditionToWords = ({
  condition = '',
  variables = [],
  transientVariableNames = [],
  valueMap = {},
}: IParseConditionToWordsProps): Array<string> => {
  let str = formatConditionString(condition);

  transientVariableNames.forEach((tVarName: string, idx: number) => {
    // tVarName - is fnName(arg1,arg2)
    // get function name
    const testFunctionMatch = str.match(functionRegExGlobal);
    testFunctionMatch?.forEach((test: string) => {
      try {
        if (convertTimeBasedFunctionFromConditionToVar({ str: test, valueMap }) === tVarName) {
          const argsStrRes = test.match(functionArgumentsRegEx);
          str = str.replace(test, `__$t:${idx}$__${argsStrRes?.[0]}`);
        }
      } catch (err) {
        // do nothing
        // console.log(err);
      }
    });
  });

  // sort variables by length so that we catch 'variableLongerWord' before 'variable'
  variables?.sort((a, b) => b.length - a.length);

  variables?.forEach((key: string, idx: number) => {
    // match variable without double quotes and not starting with '.'
    const re = new RegExp(`(?<![."])${key}(?!")`, 'g');
    str = str.replace(re, `__$v:${idx}$__`);
  });

  // known variables are now denoted by surrounding double underscores
  // the return value is an array made up of phrases or words where all of the variables
  // are isolated
  return str.split('__');
};

export const markupValueParser = (value: string | any): string => {
  let res = '';

  if (value != null) {
    res = JSON.stringify(value);
  }

  return res;
};
